import _Object$values from 'babel-runtime/core-js/object/values';
import _extends from 'babel-runtime/helpers/extends';
import _Object$keys from 'babel-runtime/core-js/object/keys';
import shuffle from 'lodash/shuffle';
import { getConfig, getErrorHandler } from '../gaia-data/init';
import { stepModel } from '../step/step-model';
import { stepTypes } from '../step/step-constants';

// bucket steps are where the user categorizes or sorts things into buckets
//  they are currently instantiated only as MatchingByMenuPage activities, not as drag-n-drop
var isBucketStep = function isBucketStep() {
  var step = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  return !!step.behavior && step.behavior.matchingBehavior === 'bucket';
};

var isImageMatchingStep = function isImageMatchingStep(step) {
  return !!(step && step.content && step.content[1] && step.content[1][0] && step.content[1][0].images);
};

var isContextImageMatchingStep = function isContextImageMatchingStep(step) {
  return isMatching(step) && step.behavior && step.behavior.hasContext;
};

var isMatching = function isMatching() {
  var step = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  return step.type === stepTypes.MATCHING;
};

var areMatchValueArraysEqual = function areMatchValueArraysEqual(arrOne, arrTwo) {
  if (arrOne.length !== arrTwo.length) {
    return false;
  }

  var _loop = function _loop(x) {
    var oneValue = arrOne[x];
    var foundValue = arrTwo.find(function (twoValue) {
      return oneValue.wordId === twoValue.wordId;
    });
    if (!foundValue || foundValue.matchId !== oneValue.matchId) {
      return {
        v: false
      };
    }
  };

  for (var x = 0; x < arrOne.length; x++) {
    var _ret = _loop(x);

    if (typeof _ret === "object") return _ret.v;
  }
  return true;
};

// contextImage matching is:  an image labelled with "A" "B" "C" etc; audio-only prompts which
//  are shuffled; and choices with only text e.g. "A" which are NOT shuffled
var parseContextImageMatchingStep = function parseContextImageMatchingStep(step) {
  if (!step.content[2] || !step.content[2][0] || step.content[2][0].type !== 'contextImage') {
    getErrorHandler()({ message: 'no contextImage for step ' + step.activityStepId });
    return null;
  }
  var contextImage = step.content[2][0];
  var prompts = step.content[0];
  var doShuffle = getConfig().prod || getConfig().localhost;
  if (doShuffle) {
    prompts = shuffle(prompts);
  }
  return {
    choices: step.content[1],
    prompts: prompts,
    contextImage: contextImage
  };
};

var parseMatchingData = function parseMatchingData(step, activity) {
  if (isContextImageMatchingStep(step)) {
    var parsedData = parseContextImageMatchingStep(step);
    if (parsedData) {
      return parsedData;
    }
  }

  var choices = getWords(step);
  var doShuffle = activity.activityType !== 'WordAndFunctions' && stepModel.isRandomOrdering(step) && stepModel.environmentNeedsShuffledChoices();
  var prompts = isBucketStep(step) ? step.content[1] : step.content[0];

  while (doShuffle) {
    choices = shuffle(choices);
    if (isBucketStep(step)) {
      // make sure the first source item does not belong to the first bucket
      doShuffle = step.correct.includes(choices[0].id + ':' + prompts[0].id);
    } else {
      // make sure the order of source items (choices) is not the same as the order of prompts
      var values = {};
      for (var i = 0; i < prompts.length; i++) {
        values[prompts[i].id] = choices[i].id;
      }
      doShuffle = stepModel.areAnswersCorrect(step, resolveAnswers(step, values, activity));
    }
  }
  return {
    choices: choices,
    prompts: prompts
  };
};

function isBucketWordCorrect(step, wordId, bucketId, values, activity) {
  var isBucketType = isBucketStep(step);
  var words = getWords(step);
  var word = words.find(function (w) {
    return w.id === wordId;
  });

  // WordAndFunction activity answers are not shuffled so use id match
  if (!word.text || activity.activityType === 'WordAndFunctions') {
    var key = isBucketType ? wordId + ':' + bucketId : bucketId + ':' + wordId;
    return step.correct.includes(key);
  }

  // text options are evaluated based on text instead of id when doing randomized matching
  var wordsInBucket = getValues(step, values).filter(function (v) {
    return v.bucketId === bucketId;
  }).map(function (v) {
    return words.find(function (w) {
      return w.id === v.wordId;
    });
  });
  var wordsWithSameText = words.filter(function (w) {
    return w.text === word.text;
  });
  var keys = wordsWithSameText.map(function (w) {
    return isBucketType ? w.id + ':' + bucketId : bucketId + ':' + w.id;
  });

  // number of words with the same text that SHOULD be in this bucket
  var numOfShouldBeInBucket = step.correct.filter(function (c) {
    return keys.includes(c);
  }).length;
  // number of words with the same text that IS in this bucket
  var numOfIsInBucket = wordsInBucket.filter(function (w) {
    return w.text === word.text;
  }).length;

  return numOfShouldBeInBucket >= numOfIsInBucket;
}

var getValues = function getValues(step, values) {
  return isBucketStep(step) ? values : _Object$keys(values).map(function (bucketId) {
    return { wordId: values[bucketId], bucketId: bucketId };
  });
};

var removeFromValues = function removeFromValues(step, values, wordId) {
  if (isBucketStep(step)) {
    return values.filter(function (_ref) {
      var valueWordId = _ref.wordId;
      return wordId !== valueWordId;
    });
  }
  var key = _Object$keys(values).find(function (key) {
    return values[key] === wordId;
  });
  var newValues = _extends({}, values);
  delete newValues[key];
  return newValues;
};

// the `values` are the answers that the user has already chosen
//  They are stored in different ways for bucket and non-bucket steps
//  for "bucket steps" they are an array of
//    { wordId: [the chosen answer id], bucketId: [the prompt id] }
//
// for all other steps they are a map from promptId to chosenAnswerId
var addToValues = function addToValues(step, values, bucketId, wordId) {
  if (isBucketStep(step)) {
    var _newValues = [].concat(values);
    // if word is already in some bucket
    var existing = values.find(function (v) {
      return v.wordId === wordId;
    });
    if (existing) {
      if (existing.bucketId === bucketId) {
        return values;
      } else {
        _newValues = removeFromValues(step, values, wordId);
      }
    }
    if (bucketId) {
      _newValues.push({ wordId: wordId, bucketId: bucketId });
    }
    return _newValues;
  }

  var newValues = _extends({}, values);
  if (_Object$values(values).includes(wordId)) {
    newValues = removeFromValues(step, values, wordId);
  }
  newValues[bucketId] = wordId;
  return newValues;
};

// this shouldn't be used directly for buckets as it contains non-shuffled words
var getWords = function getWords(step) {
  return isBucketStep(step) ? step.content[0] : step.content[1];
};

var resolveAnswers = function resolveAnswers(step, values, activity) {
  var answers = getValues(step, values);
  return answers.map(function (_ref2) {
    var wordId = _ref2.wordId,
        bucketId = _ref2.bucketId;

    return {
      correct: isBucketWordCorrect(step, wordId, bucketId, values, activity),
      answer: wordId
    };
  });
};

// correct answers are stored as an array of strings formatted as
//   [correctAnswerId]:[promptId]
// see addToValues for an explanation of the values this returns
var getAnswers = function getAnswers(step) {
  return step.correct.reduce(function (values, value) {
    var split = value.split(':');
    var wordId = split[0];
    var bucketId = split[1];
    if (isBucketStep(step)) {
      values.push({ bucketId: bucketId, wordId: wordId });
    } else {
      values[wordId] = bucketId;
    }
    return values;
  }, isBucketStep(step) ? [] : {});
};

var getWrongBucketMatchingItems = function getWrongBucketMatchingItems(choices, playerValues, step, activity) {
  var correctAnswers = getAnswers(step);
  return choices.filter(function (item) {
    var userMatchedItem = playerValues.find(function (matched) {
      return matched.wordId === item.id;
    });
    var correctItem = correctAnswers.find(function (answer) {
      return answer.wordId === item.id;
    });
    if (!correctItem) {
      // not in correctAnswers means it's unmatchable so if it is matched it is wrong
      return !!userMatchedItem;
    }
    // wrong if unmatched but should be; or if matched to wrong bucket
    return !userMatchedItem || !isBucketWordCorrect(step, userMatchedItem.wordId, userMatchedItem.bucketId, playerValues, activity);
  });
};

export var stepMatchingModel = {
  isBucketStep: isBucketStep,
  isContextImageMatchingStep: isContextImageMatchingStep,
  isImageMatchingStep: isImageMatchingStep,
  isMatching: isMatching,
  areMatchValueArraysEqual: areMatchValueArraysEqual,
  parseMatchingData: parseMatchingData,
  removeFromValues: removeFromValues,
  addToValues: addToValues,
  resolveAnswers: resolveAnswers,
  getAnswers: getAnswers,
  getWrongBucketMatchingItems: getWrongBucketMatchingItems
};